import { useCallback, useEffect, useMemo, useState } from "react";
import { TodoStatus } from "@acto/gql/graphql";
import type { Todo, User } from "@acto/taskpane/core/domain";
import type { useUpdateTodos } from "@acto/taskpane/core/data-access/todos/useUpdateTodos";
import { useViewer } from "@acto/taskpane/core/contexts/ViewerContext";
import { useUndoBatch } from "@acto/taskpane/field-sales-2/utils/hooks/useUndoBatch";
import { dropIds } from "@acto/taskpane/field-sales-2/utils/collections";

type UpdateTodosFn = ReturnType<typeof useUpdateTodos>["updateTodos"];

function reassignInput(id: string, assignee: User) {
  return { id, processedStatus: TodoStatus.Open, assignedUserId: assignee.id };
}

interface PendingReassignment {
  todo: Todo;
  newAssignee: User;
}

export interface OptimisticTodoReassignment {
  /**
   * The assignee to display for [todo]: the optimistic target while a reassignment is pending or
   * committing, otherwise the todo's own server assignee.
   */
  assigneeFor: (todo: Todo) => User | undefined;
  /**
   * The assignee name to display on a list row, or undefined when there is nothing worth showing.
   * A todo assigned to the viewer shows no name — whose it is, is already implied by it being on
   * their list — so only somebody else's name surfaces.
   */
  assigneeNameFor: (todo: Todo) => string | undefined;
  isReassigning: (id: string) => boolean;
  handleReassign: (todo: Todo, newAssignee: User) => void;
  handleUndo: () => void;
  pendingCount: number;
}

/**
 * Optimistic reassignment + batch-undo, the sibling of [useOptimisticTodoCompletion]. Reassigning a
 * todo overrides its displayed assignee in place and opens the shared grace window; while it runs
 * the undo banner reverts the batch with NO server call. When the window elapses the OPEN+assignee
 * mutations fire; a failed mutation rolls that todo's override back. Unlike completion, the row is not moved
 * between buckets — the cache invalidation on commit refetches the personal lists, which then drop
 * manual/workflow todos reassigned away (note-based todos legitimately stay, so the reconcile
 * tolerates rows that remain and clears the override once the server reflects the new assignee).
 *
 * [shownTodos] is the union of currently-rendered todos, used only to reconcile committed overrides.
 */
export function useOptimisticTodoReassignment(
  updateTodos: UpdateTodosFn,
  shownTodos: Todo[],
  /** Called when a reassignment starts, so a caller running other grace batches can settle them. */
  onSettleOtherBatches?: () => void,
): OptimisticTodoReassignment {
  const { isViewer } = useViewer();
  const [committed, setCommitted] = useState<Map<string, PendingReassignment>>(() => new Map());

  // Normal commit path: `useUpdateTodos` swallows mutation errors and resolves to null, so on failure
  // drop the committed override — the row reverts to its server assignee instead of showing a ghost.
  const reassign = async (id: string, assignee: User) => {
    const updated = await updateTodos({ input: reassignInput(id, assignee) });
    if (updated) return;
    setCommitted((prev) => dropIds(prev, [id]));
  };

  const reassignments = useUndoBatch<PendingReassignment>((batch) => {
    setCommitted((prev) => {
      const next = new Map(prev);
      batch.forEach((entry, id) => next.set(id, entry));
      return next;
    });
    batch.forEach((entry, id) => void reassign(id, entry.newAssignee));
  });

  const { pending, schedule: scheduleReassignment } = reassignments;

  const handleReassign = (todo: Todo, newAssignee: User) => {
    onSettleOtherBatches?.();
    scheduleReassignment(todo.id, { todo, newAssignee });
  };

  // Drop a committed override once the server reflects the new assignee, or the row has left the list.
  useEffect(() => {
    setCommitted((prev) => {
      if (prev.size === 0) return prev;
      const byId = new Map(shownTodos.map((todo) => [todo.id, todo]));
      let changed = false;
      const next = new Map(prev);
      prev.forEach((entry, id) => {
        const current = byId.get(id);
        if (!current || current.assignee?.id === entry.newAssignee.id) {
          next.delete(id);
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [shownTodos]);

  const overrides = useMemo(() => {
    const map = new Map<string, User>();
    committed.forEach((entry, id) => map.set(id, entry.newAssignee));
    // A fresh pending pick wins over an earlier committed override for the same todo.
    pending.forEach((entry, id) => map.set(id, entry.newAssignee));
    return map;
  }, [committed, pending]);

  const assigneeFor = useCallback((todo: Todo) => overrides.get(todo.id) ?? todo.assignee, [overrides]);

  const assigneeNameFor = (todo: Todo) => {
    const assignee = assigneeFor(todo);
    return assignee && !isViewer(assignee) ? (assignee.displayName ?? undefined) : undefined;
  };

  const isReassigning = (id: string) => pending.has(id);

  return {
    assigneeFor,
    assigneeNameFor,
    isReassigning,
    handleReassign,
    handleUndo: reassignments.undo,
    pendingCount: pending.size,
  };
}
